import React, { useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
    FaBell, 
    FaTimes, 
    FaShoppingCart, 
    FaEye, 
    FaVolumeUp, 
    FaVolumeMute,
    FaSync,
    FaCheck,
    FaArrowRight,
    FaClock,
    FaUser
} from 'react-icons/fa';
import { useOrderNotifications } from '../../context/OrderNotificationContext';

const OrderNotificationWidget = () => {
    const navigate = useNavigate();
    const widgetRef = useRef(null);
    
    const {
        notificationData,
        isLoading,
        isWidgetOpen,
        soundEnabled,
        hasNewOrders,
        toggleWidget,
        closeWidget,
        toggleSound,
        markOrdersAsViewed,
        refreshNotifications
    } = useOrderNotifications();

    const { 
        totalActiveOrders, 
        unviewedCount, 
        receivedCount, 
        confirmedCount, 
        latestOrders 
    } = notificationData;

    // Close widget when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (widgetRef.current && !widgetRef.current.contains(event.target)) {
                closeWidget();
            }
        };

        if (isWidgetOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isWidgetOpen, closeWidget]);

    // Handle order click - navigate to orders page with filter
    const handleOrderClick = (order) => {
        closeWidget();
        navigate('/orders');
        // Store the tab to switch to
        localStorage.setItem('activeTab', order.status === 'Received' ? 'Received' : 'Confirmed');
    };

    // Handle view all click
    const handleViewAll = () => {
        closeWidget();
        markOrdersAsViewed();
        navigate('/orders');
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(amount);
    };

    // Get status color and label
    const getStatusInfo = (status) => {
        switch (status) {
            case 'Received':
                return { 
                    color: 'bg-orange-100 text-orange-700 border-orange-200',
                    label: 'Pending'
                };
            case 'Confirmed':
                return { 
                    color: 'bg-green-100 text-green-700 border-green-200',
                    label: 'Confirmed'
                };
            default:
                return { 
                    color: 'bg-gray-100 text-gray-700 border-gray-200',
                    label: status
                };
        }
    };

    return (
        <>
            {/* Floating Notification Bell Button */}
            <motion.button
                ref={!isWidgetOpen ? widgetRef : null}
                onClick={toggleWidget}
                className={`fixed bottom-6 right-6 z-[100] p-4 rounded-full shadow-2xl transition-all duration-300 ${
                    hasNewOrders 
                        ? 'bg-gradient-to-r from-red-500 to-pink-500 animate-pulse' 
                        : totalActiveOrders > 0 
                            ? 'bg-gradient-to-r from-blue-500 to-indigo-600' 
                            : 'bg-gradient-to-r from-gray-400 to-gray-500'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                title="Order Notifications"
            >
                <div className="relative">
                    <FaBell className="text-white text-xl" />
                    
                    {/* Notification Badge */}
                    {totalActiveOrders > 0 && (
                        <motion.span
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            className="absolute -top-2 -right-2 min-w-[20px] h-5 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full px-1 shadow-lg"
                        >
                            {totalActiveOrders > 99 ? '99+' : totalActiveOrders}
                        </motion.span>
                    )}
                    
                    {/* Pulse ring for new orders */}
                    {hasNewOrders && (
                        <span className="absolute inset-0 rounded-full bg-white opacity-25 animate-ping"></span>
                    )}
                </div>
            </motion.button>

            {/* Notification Panel */}
            <AnimatePresence>
                {isWidgetOpen && (
                    <motion.div
                        ref={widgetRef}
                        initial={{ opacity: 0, y: 20, scale: 0.95 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                        className="fixed bottom-24 right-6 z-[101] w-96 max-w-[calc(100vw-48px)] bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-4 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <FaShoppingCart className="text-white text-lg" />
                                <h3 className="text-white font-semibold text-lg">Pending Orders</h3>
                                {receivedCount > 0 && (
                                    <span className="bg-white/20 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                                        {receivedCount}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Refresh Button */}
                                <button 
                                    onClick={refreshNotifications}
                                    className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                                    title="Refresh"
                                    disabled={isLoading}
                                >
                                    <FaSync className={`text-sm ${isLoading ? 'animate-spin' : ''}`} />
                                </button>
                                
                                {/* Sound Toggle */}
                                <button 
                                    onClick={toggleSound}
                                    className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                                    title={soundEnabled ? 'Mute notifications' : 'Enable sound'}
                                >
                                    {soundEnabled ? (
                                        <FaVolumeUp className="text-sm" />
                                    ) : (
                                        <FaVolumeMute className="text-sm" />
                                    )}
                                </button>
                                
                                {/* Close Button */}
                                <button 
                                    onClick={closeWidget}
                                    className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
                                >
                                    <FaTimes className="text-sm" />
                                </button>
                            </div>
                        </div>

                        {/* Stats Bar */}
                        <div className="bg-gray-50 px-4 py-2 flex items-center justify-between border-b border-gray-100">
                            <div className="flex items-center gap-4">
                                <div className="flex items-center gap-1">
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-orange-100 text-orange-700 text-xs font-bold rounded-full">
                                        {receivedCount}
                                    </span>
                                    <span className="text-xs text-gray-600">Pending</span>
                                </div>
                                <div className="flex items-center gap-1">
                                    <span className="inline-flex items-center justify-center w-6 h-6 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                                        {confirmedCount}
                                    </span>
                                    <span className="text-xs text-gray-600">Confirmed</span>
                                </div>
                            </div>
                            {unviewedCount > 0 && (
                                <button
                                    onClick={markOrdersAsViewed}
                                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium"
                                >
                                    <FaCheck className="text-xs" />
                                    Mark all viewed
                                </button>
                            )}
                        </div>

                        {/* Orders List */}
                        <div className="max-h-80 overflow-y-auto">
                            {isLoading && latestOrders.length === 0 ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                                </div>
                            ) : latestOrders.length > 0 ? (
                                <div className="divide-y divide-gray-100">
                                    {latestOrders.map((order, index) => {
                                        const statusInfo = getStatusInfo(order.status);
                                        return (
                                        <motion.div
                                            key={order._id}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            onClick={() => handleOrderClick(order)}
                                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer transition-colors group relative"
                                        >
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                                                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${statusInfo.color}`}>
                                                            {statusInfo.label}
                                                        </span>
                                                        <span className="text-[10px] text-gray-500 font-mono">
                                                            {order.saleId}
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-1.5 mb-0.5">
                                                        <FaUser className="text-gray-400 text-xs" />
                                                        <span className="text-sm font-medium text-gray-800 truncate">
                                                            {order.customerName}
                                                        </span>
                                                    </div>
                                                    <p className="text-xs text-gray-500 truncate">
                                                        {order.firstProductName} 
                                                        {order.itemsCount > 1 && (
                                                            <span className="ml-1 text-gray-400">
                                                                +{order.itemsCount - 1} more
                                                            </span>
                                                        )}
                                                    </p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        <span className="text-[10px] text-gray-400 flex items-center gap-1">
                                                            <FaClock className="text-[9px]" />
                                                            {order.orderTime || order.timeAgo}
                                                        </span>
                                                        <span className="text-[10px] text-gray-400">
                                                            {order.timeAgo}
                                                        </span>
                                                    </div>
                                                </div>
                                                <div className="text-right flex-shrink-0">
                                                    <p className="text-sm font-bold text-green-600">
                                                        {formatCurrency(order.totalAmount)}
                                                    </p>
                                                </div>
                                            </div>
                                            <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                                                <FaArrowRight className="text-blue-400 text-sm" />
                                            </div>
                                        </motion.div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="py-8 text-center">
                                    <div className="w-16 h-16 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                                        <FaShoppingCart className="text-gray-400 text-2xl" />
                                    </div>
                                    <p className="text-gray-500 text-sm">No active orders</p>
                                    <p className="text-gray-400 text-xs mt-1">New orders will appear here</p>
                                </div>
                            )}
                        </div>

                        {/* Footer */}
                        {totalActiveOrders > 0 && (
                            <div className="border-t border-gray-100 bg-gray-50 px-4 py-3">
                                <button
                                    onClick={handleViewAll}
                                    className="w-full flex items-center justify-center gap-2 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium text-sm hover:from-blue-600 hover:to-indigo-700 transition-all shadow-md hover:shadow-lg"
                                >
                                    <FaEye />
                                    View All Orders ({totalActiveOrders})
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default OrderNotificationWidget;
